<template>
  <div class="registration-list pa-10">
    <title-page>
      {{ $t('onboarding.registration.registrations') }}
    </title-page>

    <v-card class="mb-4">
      <v-card-text>
        <v-row class="justify-center align-center">
          <v-col cols="12" md="4">
            <v-text-field
                v-model="filters.search"
                :label="$t('search')"
                solo
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                :placeholder="$t('onboarding.registration.search-hint')"
                @keyup.enter="applyFilters"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
                v-model="filters.status"
                :items="statusOptions"
                :label="$t('status')"
                multiple
                chips
                small-chips
                deletable-chips
                hide-details
                solo
                clearable
            ></v-select>
          </v-col>

          <v-col class="text-right">
            <v-btn small text class="ml-2" @click="clearFilters">
              <v-icon left small>mdi-filter-off</v-icon>
              {{ $t('clear') }}
            </v-btn>
            <v-btn small outlined color="primary" @click="applyFilters">
              <v-icon left small>mdi-magnify</v-icon>
              {{ $t('search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <v-data-table
            :headers="headers"
            :items="items"
            :options.sync="options"
            :loading="loading"
            :server-items-length="total"
            :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
        >
          <template #item.applicant="{ item }">
            <a href="#" @click.prevent="open(item)">{{ registrationName(item) }}</a>
          </template>

          <template #item.status="{ item }">
            <div>
              <v-chip small :color="registrationStatusColor(item.status)" dark>
                {{ $t(`onboarding.registration.status.${item.status}`) }}
              </v-chip>
              <!-- Une inscription dont l'échéance est passée n'est pas encore expirée : elle
                   attend le passage qui la fermera. Le dire évite de croire à un bug. -->
              <v-chip v-if="item.overdue" x-small color="red" dark class="ml-1">
                {{ $t('onboarding.registration.overdue') }}
              </v-chip>
            </div>
          </template>

          <template #item.nextGate="{ item }">
            <span v-if="item.nextGate">{{ $t(`onboarding.registration.gate.${item.nextGate}`) }}</span>
            <span v-else class="text--disabled">-</span>
          </template>

          <template #item.source="{ item }">
            {{ $t(`onboarding.registration.source.${item.source}`) }}
          </template>

          <template #item.createdAt="{ item }">
            {{ item.createdAt | formatDate }}
          </template>

          <template #item.actions="{ item }">
            <v-btn icon small @click="open(item)">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import RegistrationMixin from './RegistrationMixin'

export default {
  name: 'RegistrationList',

  mixins: [RegistrationMixin],

  data() {
    return {
      items: [],
      total: 0,
      loading: false,
      error: null,
      options: {},
      filters: {
        search: null,
        status: [],
      },
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('onboarding.registration.applicant'), value: 'applicant', sortable: false },
        { text: this.$t('email'), value: 'email', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false },
        { text: this.$t('onboarding.registration.next-gate'), value: 'nextGate', sortable: false },
        { text: this.$t('onboarding.registration.source-label'), value: 'source', sortable: false },
        { text: this.$t('created-at'), value: 'createdAt', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ]
    },

    statusOptions() {
      return [
        'draft', 'invited', 'in-progress',
        'awaiting-approval', 'awaiting-payment-method',
        'validated', 'created',
        'active', 'cancelled', 'rejected', 'expired',
      ].map(value => ({ value, text: this.$t(`onboarding.registration.status.${value}`) }))
    },
  },

  watch: {
    options: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null

      const { page = 1, itemsPerPage = 20 } = this.options

      try {
        const data = await RegistrationRepository.list({
          search: this.filters.search,
          status: this.filters.status,
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
        })
        this.items = data.items
        this.total = data.total
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('error-occurred')
      } finally {
        this.loading = false
      }
    },

    applyFilters() {
      this.options = { ...this.options, page: 1 }
      this.load()
    },

    clearFilters() {
      this.filters = { search: null, status: [] }
      this.applyFilters()
    },

    open(item) {
      this.$router.push({ name: 'onboarding.registrations.detail', params: { id: item.id } })
    },

  },
}
</script>
